/**
 * Short label clarifying an icon-only control or a truncated value.
 * Spec: assets/handoffs/design_system_export/specs/Tooltip.json
 *
 * Simple absolute positioning relative to the trigger (no arrow glyph,
 * no viewport-collision flipping) — scoped out for the same reason as
 * DropdownMenu's positioning: would need a positioning library to do fully.
 *
 * Per the spec's own accessibility note, don't wrap a disabled button
 * directly — disabled elements receive no hover/focus events, so wrap it in
 * a focusable span instead.
 *
 * @param {ReactNode} content
 * @param {ReactElement} children - single trigger element; gets hover/focus handlers + aria-describedby cloned onto it
 * @param {'top'|'bottom'|'left'|'right'=top} placement
 * @param {'dark'|'light'=dark} variant
 */
import React, { useId, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./Tooltip.module.scss";

const OPEN_DELAY = 300;
const CLOSE_DELAY = 100;

const Tooltip = ({ content, children, placement = "top", variant = "dark", className }) => {
  const [visible, setVisible] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const id = useId();

  const show = () => {
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => setVisible(true), OPEN_DELAY);
  };

  const hide = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setVisible(false), CLOSE_DELAY);
  };

  const trigger = React.cloneElement(children, {
    onMouseEnter: (e) => {
      children.props.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e) => {
      children.props.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e) => {
      children.props.onFocus?.(e);
      show();
    },
    onBlur: (e) => {
      children.props.onBlur?.(e);
      hide();
    },
    onKeyDown: (e) => {
      children.props.onKeyDown?.(e);
      if (e.key === "Escape") {
        clearTimeout(openTimer.current);
        setVisible(false);
      }
    },
    "aria-describedby": content ? id : undefined,
  });

  return (
    <span className={classNames(styles.root, className)}>
      {trigger}
      {visible && content && (
        <span
          role="tooltip"
          id={id}
          className={classNames(
            styles.tooltip,
            styles[`tooltip--${variant}`],
            styles[`tooltip--${placement}`]
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
};

export default Tooltip;
